import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Save,
  Refresh,
  Description,
  Email,
  Notifications,
  Info,
  Code,
  Preview,
  Visibility,
  Edit,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import {
  PageLayout,
  PageHeader,
  ScrollableContent,
} from '../components/Layout/index';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface TextTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  endpoint: string;
  content: string;
}

const Settings: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [modifiedTemplates, setModifiedTemplates] = useState<Set<string>>(new Set());
  const [showHtmlEditor, setShowHtmlEditor] = useState<Record<string, boolean>>({});
  const [localTemplateContent, setLocalTemplateContent] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  // Template configurations
  const templateConfigs: Omit<TextTemplate, 'content'>[] = [
    {
      id: 'receipt-thank-you',
      name: 'Messaggio Ringraziamento Ricevute',
      description: 'Testo di ringraziamento incluso nelle ricevute PDF delle donazioni',
      icon: <Description />,
      endpoint: '/receipts/template',
    },
    {
      id: 'receipt-email',
      name: 'Email Ricevuta Donazione',
      description: 'Template email inviata ai donatori con ricevuta allegata',
      icon: <Email />,
      endpoint: '/templates/receipt-email',
    },
    {
      id: 'email-welcome',
      name: 'Email di Benvenuto',
      description: 'Email inviata ai nuovi donatori dopo la registrazione',
      icon: <Email />,
      endpoint: '/templates/email-welcome',
    },
    // {
    //   id: 'notification-donation',
    //   name: 'Notifica Donazione',
    //   description: 'Notifica per nuove donazioni',
    //   icon: <Notifications />,
    //   endpoint: '/notifications/donation-template',
    // },
  ];

  // Query per ottenere i template
  const { data: templatesData, isLoading: isLoadingTemplates, error: templatesError } = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiService.getTemplates(),
    enabled: true,
  });

  // Debug log
  console.log('Templates data:', {
    isLoading: isLoadingTemplates,
    error: templatesError,
    data: templatesData,
    dataLength: templatesData?.length
  });

  // Mutation per aggiornare i template
  const updateTemplateMutation = useMutation({
    mutationFn: ({ templateId, content }: { templateId: string; content: string }) => 
      apiService.updateTemplate(templateId, content),
    onSuccess: (_, variables) => {
      // Trova il templateId locale dal templateId del database
      const templateConfig = templateConfigs.find(config => {
        const dbTemplate = templatesData?.find(t => t.type === config.id);
        return dbTemplate?.id === variables.templateId;
      });
      
      if (templateConfig) {
        // Rimuovi il contenuto locale per questo template
        setLocalTemplateContent(prev => {
          const newContent = { ...prev };
          delete newContent[templateConfig.id];
          return newContent;
        });
      }
      
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setModifiedTemplates(prev => new Set());
    },
  });

  // Crea i template con il contenuto attuale dal database o locale
  const templates: TextTemplate[] = templateConfigs.map(config => {
    // Trova il template corrispondente nei dati del database
    const dbTemplate = templatesData?.find(t => t.type === config.id);
    
    // Usa il contenuto locale se disponibile, altrimenti quello del database
    const content = localTemplateContent[config.id] || dbTemplate?.content || '';
    
    console.log(`Template ${config.id}:`, {
      found: !!dbTemplate,
      contentLength: content.length,
      content: content.substring(0, 100) + '...',
      isLocal: !!localTemplateContent[config.id]
    });
    
    return {
      ...config,
      content,
    };
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleTemplateChange = (templateId: string, content: string) => {
    console.log(`Template ${templateId} change:`, content.substring(0, 100) + '...');
    setLocalTemplateContent(prev => ({
      ...prev,
      [templateId]: content
    }));
    setModifiedTemplates(prev => new Set(prev).add(templateId));
  };

  const toggleHtmlEditor = (templateId: string) => {
    setShowHtmlEditor(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }));
  };

  const isHtmlTemplate = (templateId: string) => {
    // Trova il template nel database per controllare il type
    const dbTemplate = templatesData?.find(t => t.type === templateId);
    const isHtml = dbTemplate?.type === 'receipt-email' || dbTemplate?.type === 'email-welcome';
    console.log(`isHtmlTemplate(${templateId}):`, {
      dbTemplate: dbTemplate ? { id: dbTemplate.id, type: dbTemplate.type } : null,
      isHtml
    });
    return isHtml;
  };


  const formatHtmlForDisplay = (html: string): string => {
    // Formatta l'HTML per una migliore visualizzazione
    return html
      .replace(/></g, '>\n<')
      .replace(/^\s+|\s+$/g, '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .join('\n');
  };

  const handleSave = (templateId: string) => {
    const localContent = localTemplateContent[templateId];
    if (!localContent) return;

    const dbTemplate = templatesData?.find(t => t.type === templateId);
    if (!dbTemplate) return;

    updateTemplateMutation.mutate({ 
      templateId: dbTemplate.id, 
      content: localContent 
    });
  };

  const handleRefresh = (templateId: string) => {
    // Rimuovi il contenuto locale per forzare il reload dal database
    setLocalTemplateContent(prev => {
      const newContent = { ...prev };
      delete newContent[templateId];
      return newContent;
    });
    setModifiedTemplates(prev => {
      const newSet = new Set(prev);
      newSet.delete(templateId);
      return newSet;
    });
    queryClient.invalidateQueries({ queryKey: ['templates'] });
  };

  const isModified = (templateId: string) => modifiedTemplates.has(templateId);

  const isSaving = (templateId: string) => {
    return updateTemplateMutation.isPending;
  };

  // Mostra loading se i template stanno caricando
  if (isLoadingTemplates) {
    return (
      <PageLayout>
        <PageHeader>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Impostazioni
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Gestisci i template di testo del sistema
            </Typography>
          </Box>
        </PageHeader>
        <ScrollableContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </ScrollableContent>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Impostazioni
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Gestisci i template di testo del sistema
          </Typography>
        </Box>
      </PageHeader>
      
      <ScrollableContent>
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="settings tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              {templates.map((template, index) => (
                <Tab
                  key={template.id}
                  label={template.name}
                  sx={{ minHeight: 48 }}
                />
              ))}
            </Tabs>
          </Box>

          {templates.map((template, index) => {
            console.log(`Rendering template ${template.id}:`, {
              content: template.content.substring(0, 100) + '...',
              contentLength: template.content.length,
              isModified: modifiedTemplates.has(template.id)
            });
            
            return (
            <TabPanel key={template.id} value={tabValue} index={index}>
              <Card>
                <CardHeader
                  title={
                    <Box display="flex" alignItems="center" gap={1}>
                      {template.icon}
                      {template.name}
                    </Box>
                  }
                  subheader={template.description}
                  action={
                    <Box display="flex" gap={1}>
                      <Tooltip title="Ricarica contenuto">
                        <IconButton
                          onClick={() => handleRefresh(template.id)}
                          disabled={isSaving(template.id)}
                        >
                          <Refresh />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="contained"
                        startIcon={isSaving(template.id) ? <CircularProgress size={16} /> : <Save />}
                        onClick={() => handleSave(template.id)}
                        disabled={!isModified(template.id) || isSaving(template.id)}
                        color={isModified(template.id) ? 'warning' : 'primary'}
                      >
                        {isSaving(template.id) ? 'Salvataggio...' : 'Salva'}
                      </Button>
                    </Box>
                  }
                />
                <CardContent>
                  {isModified(template.id) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Questo template è stato modificato. Ricorda di salvare le modifiche.
                    </Alert>
                  )}

                  {isHtmlTemplate(template.id) ? (
                    <Box>
                      {/* Controlli per template HTML */}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showHtmlEditor[template.id] || false}
                              onChange={() => toggleHtmlEditor(template.id)}
                              color="primary"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              {showHtmlEditor[template.id] ? <Code /> : <Visibility />}
                              {showHtmlEditor[template.id] ? 'Solo Editor' : 'Editor + Anteprima'}
                            </Box>
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          {showHtmlEditor[template.id] 
                            ? 'Modalità editor: solo codice HTML' 
                            : 'Modalità split: editor + anteprima'
                          }
                        </Typography>
                      </Box>

                      <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                        {/* Editor HTML - sempre visibile */}
                        <Box flex={showHtmlEditor[template.id] ? 1 : 0.5}>
                          <Typography variant="subtitle2" gutterBottom color="text.secondary">
                            {showHtmlEditor[template.id] ? 'Editor HTML:' : 'Editor HTML (modifica qui):'}
                          </Typography>
                          <TextField
                            fullWidth
                            multiline
                            rows={15}
                            value={template.content}
                            onChange={(e) => handleTemplateChange(template.id, e.target.value)}
                            placeholder="Inserisci il codice HTML per l'email..."
                            variant="outlined"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                              },
                            }}
                          />
                        </Box>

                        {/* Anteprima - visibile solo quando non in modalità editor */}
                        {!showHtmlEditor[template.id] && (
                          <Box flex={0.5}>
                            <Typography variant="subtitle2" gutterBottom color="text.secondary">
                              Anteprima Email:
                            </Typography>
                            <Paper 
                              elevation={1} 
                              sx={{ 
                                p: 2, 
                                backgroundColor: 'background.default',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                maxHeight: '400px',
                                overflow: 'auto'
                              }}
                            >
                              <div 
                                dangerouslySetInnerHTML={{ 
                                  __html: template.content || '<em>Nessun contenuto HTML disponibile</em>' 
                                }}
                                style={{ 
                                  fontFamily: 'Arial, sans-serif',
                                  fontSize: '14px',
                                  lineHeight: '1.4'
                                }}
                              />
                            </Paper>
                            
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    // Editor normale per template di testo
                    (() => {
                      console.log(`TextField for ${template.id} - disabled:`, isSaving(template.id), 'content length:', template.content.length);
                      return (
                        <TextField
                          fullWidth
                          multiline
                          rows={15}
                          value={template.content}
                          onChange={(e) => {
                            console.log(`Template ${template.id} changed:`, e.target.value.substring(0, 100) + '...');
                            handleTemplateChange(template.id, e.target.value);
                          }}
                          placeholder={`Inserisci il contenuto per ${template.name.toLowerCase()}...`}
                          variant="outlined"
                          disabled={isSaving(template.id)}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontFamily: 'monospace',
                              fontSize: '0.9rem',
                            },
                          }}
                        />
                      );
                    })()
                  )}
                  
                  <Box mt={2}>
                    {/* <Typography variant="body2" color="text.secondary">
                      <strong>Suggerimenti:</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div">
                      {isHtmlTemplate(template.id) ? (
                        <>
                          • Usa l'<strong>Anteprima</strong> per vedere come apparirà l'email<br/>
                          • Usa l'<strong>Editor HTML</strong> per modificare il codice<br/>
                          • I placeholder come <code>{'{{donorName}}'}</code> vengono sostituiti automaticamente<br/>
                          • Le modifiche vengono salvate nel database
                        </>
                      ) : (
                        <>
                          • Usa <code>\n</code> per andare a capo<br/>
                          • Le modifiche vengono salvate nel database<br/>
                          • Il contenuto viene utilizzato automaticamente nelle funzionalità correlate
                        </>
                      )}
                    </Typography> */}
                  </Box>
                </CardContent>
              </Card>
            </TabPanel>
            );
          })}
        </Paper>
      </ScrollableContent>
    </PageLayout>
  );
};

export default Settings;
